import { useCallback, useRef, useState } from 'react';
import { useDismiss } from './useDismiss';

interface CloseOptions {
  /** Defaults to true. Pass false when the choice hands focus somewhere else,
   *  such as a dialog that opens as a result. */
  restoreFocus?: boolean;
}

/**
 * Open state for a trigger-and-panel pair. Closing by Escape or by making a
 * choice hands focus back to the trigger, as the ARIA practices ask; closing
 * because the user clicked or tabbed elsewhere leaves focus where they put it.
 *
 * Attach `rootRef` to an element wrapping both the trigger and the panel.
 */
export function usePopover<TTrigger extends HTMLElement = HTMLButtonElement>() {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<TTrigger>(null);

  const toggle = useCallback(() => setIsOpen((current) => !current), []);

  const close = useCallback(({ restoreFocus = true }: CloseOptions = {}) => {
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useDismiss(rootRef, isOpen, (reason) => close({ restoreFocus: reason === 'escape' }));

  return { isOpen, toggle, close, rootRef, triggerRef };
}
